import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

import { industries } from '@/data/industries';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import heroProcessImage from '@/assets/hero-process.jpg';
import dairyImage from '@/assets/industries/dairy.jpg';
import beverageImage from '@/assets/industries/beverage.jpg';
import bakeryImage from '@/assets/industries/bakery.jpg';
import meatFishImage from '@/assets/industries/meat-fish.jpg';
import oilsImage from '@/assets/industries/oils.jpg';
import spreadsImage from '@/assets/industries/spreads.jpg';
import fruitsVegetablesImage from '@/assets/industries/fruits-vegetables.jpg';
import readyMealsImage from '@/assets/industries/ready-meals.jpg';

const imageMap = {
  'dairy': dairyImage,
  'beverage': beverageImage,
  'bakery-grains-snacks': bakeryImage,
  'meat-fish-eggs': meatFishImage,
  'oils-fats': oilsImage,
  'spreads-sauces': spreadsImage,
  'fruit-vegetables': fruitsVegetablesImage,
  'ready-meals': readyMealsImage
};

const Industries = () => {
  const { language, t } = useLanguage();

  const industriesContent = {
    dairy: {
      el: "Η TFT προσφέρει ολοκληρωμένες λύσεις για τη βιομηχανία γαλακτοκομικών, καλύπτοντας κάθε στάδιο από την επεξεργασία του γάλακτος έως την παραγωγή γιαουρτιού, τυριού, παγωτού και βουτύρου, με έμφαση στην ποιότητα και την αποδοτικότητα.",
      en: "TFT offers complete solutions for the dairy industry, covering every stage from milk processing to the production of yoghurt, cheese, ice cream and butter, with emphasis on quality and efficiency."
    },
    beverage: {
      el: "Εξοπλισμός και συστήματα για την παραγωγή και συσκευασία νερού, αναψυκτικών, χυμών, μπύρας και κρασιού, με λύσεις που διασφαλίζουν την ποιότητα, την ασφάλεια και την αποδοτικότητα.",
      en: "Equipment and systems for the production and packaging of water, soft drinks, juices, beer and wine, with solutions that ensure quality, safety and efficiency."
    },
    "bakery-grains-snacks": {
      el: "Εξοπλισμός επεξεργασίας και συσκευασίας για αρτοσκευάσματα, δημητριακά και σνακ, με τεχνολογίες που εξασφαλίζουν φρεσκάδα και υψηλή ποιότητα προϊόντων.",
      en: "Processing and packaging equipment for bakery products, grains and snacks, with technologies that ensure freshness and high product quality."
    },
    "meat-fish-eggs": {
      el: "Λύσεις συσκευασίας και ποιοτικού ελέγχου για κρέας, ψάρι και αυγά, με έμφαση στη διατήρηση της φρεσκάδας, την ασφάλεια τροφίμων και τη μεγιστοποίηση του χρόνου ζωής των προϊόντων.",
      en: "Packaging and quality control solutions for meat, fish and eggs, with emphasis on maintaining freshness, food safety and maximizing product shelf life."
    },
    "oils-fats": {
      el: "Εξειδικευμένες λύσεις για την επεξεργασία και συσκευασία φυτικών ελαίων, μαργαρίνης και λιπών, με τεχνολογίες που διασφαλίζουν την ποιότητα και την ασφάλεια των προϊόντων.",
      en: "Specialized solutions for processing and packaging vegetable oils, margarine and fats, with technologies that ensure product quality and safety."
    },
    "spreads-sauces": {
      el: "Εξοπλισμός για την παραγωγή και συσκευασία αλειμμάτων και σαλτσών, με συστήματα που εξασφαλίζουν την ομοιογένεια, τη σταθερότητα και την ποιότητα των προϊόντων.",
      en: "Equipment for the production and packaging of spreads and sauces, with systems that ensure homogeneity, stability and product quality."
    },
    "fruit-vegetables": {
      el: "Λύσεις συσκευασίας για φρέσκα και επεξεργασμένα φρούτα και λαχανικά, με τεχνολογίες που διατηρούν τη φρεσκάδα και παρατείνουν τη διάρκεια ζωής.",
      en: "Packaging solutions for fresh and processed fruits and vegetables, with technologies that maintain freshness and extend shelf life."
    },
    "ready-meals": {
      el: "Ολοκληρωμένες λύσεις για την παραγωγή και συσκευασία έτοιμων γευμάτων, με έμφαση στην ασφάλεια τροφίμων, τη διατήρηση της ποιότητας και την αποδοτικότητα παραγωγής.",
      en: "Complete solutions for the production and packaging of ready meals, with emphasis on food safety, quality preservation and production efficiency."
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title={t('Κλάδοι', 'Industries')}
        description={t('Ολοκληρωμένες λύσεις για τη βιομηχανία τροφίμων: γαλακτοκομικά, ποτά, αρτοσκευάσματα, κρέας, έλαια, σάλτσες και έτοιμα γεύματα.', 'Complete solutions for the food industry: dairy, beverages, bakery, meat, oils, sauces and ready meals.')}
        canonical="https://www.tft.gr/industries"
      />
      <Navigation />

      <main>
        {/* Hero Section with Image */}
        <section className="relative h-[60vh] min-h-[500px] bg-gradient-to-b from-primary to-primary-light overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/60 z-10" />
          <img
            src={heroProcessImage}
            alt="Industries"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="relative z-20 container mx-auto px-4 h-full flex flex-col justify-center items-center text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white animate-fade-in">
              {t('Κλάδοι Δραστηριότητας', 'Fields of Activity')}
            </h1>
            <p className="text-xl md:text-2xl max-w-4xl text-white/95 animate-fade-in">
              {t(
                'Η TFT δραστηριοποιείται σε ένα ευρύ φάσμα κλάδων της βιομηχανίας τροφίμων και ποτών, παρέχοντας ολοκληρωμένες λύσεις επεξεργασίας, συσκευασίας και ποιοτικού ελέγχου.',
                'TFT operates in a wide range of sectors in the food and beverage industry, providing complete processing, packaging and quality control solutions.'
              )}
            </p>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-16 bg-muted">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-lg leading-relaxed text-muted-foreground">
                {t(
                  'Με εξειδίκευση σε κλάδους όπως τα γαλακτοκομικά, τα ποτά, τα αρτοσκευάσματα, το κρέας, τα έλαια, τα αλείμματα, τα φρούτα και τα έτοιμα γεύματα, η εταιρεία υποστηρίζει τους πελάτες της σε κάθε στάδιο παραγωγής, προσφέροντας τεχνολογία αιχμής, αξιοπιστία και υψηλά πρότυπα ασφάλειας τροφίμων.',
                  'With expertise in sectors such as dairy, beverages, bakery, meat, oils, spreads, fruits and ready meals, the company supports its customers at every stage of production, offering cutting-edge technology, reliability and high food safety standards.'
                )}
              </p>
            </div>
          </div>
        </section>

        {/* Industries Grid */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold text-center mb-16">
              {t('Κλάδοι Δραστηριότητας', 'Industries')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {industries.map((industry) => {
                const industryImage = imageMap[industry.slug as keyof typeof imageMap];
                const content = industriesContent[industry.slug as keyof typeof industriesContent];
                return (
                  <Card key={industry.id} className="hover:shadow-2xl transition-all duration-300 group border-l-4 border-l-primary overflow-hidden h-full">
                    <CardContent className="p-0 h-full">
                      <Link to={`/industries/${industry.slug}`} className="block h-full">
                        <div className="flex flex-col h-full">
                          <div className="w-full h-48 relative overflow-hidden">
                            <img
                              src={industryImage}
                              alt={industry.title[language]}
                              className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-500"
                            />
                          </div>
                          <div className="flex-1 p-6 flex flex-col">
                            <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors">
                              {industry.title[language]}
                            </h3>
                            <p className="text-sm text-muted-foreground mb-4 leading-relaxed flex-grow line-clamp-3">
                              {content[language]}
                            </p>
                            <Button variant="ghost" className="group/btn p-0 h-auto hover:bg-transparent justify-start text-sm">
                              <span className="mr-2">{t('Περισσότερα', 'Learn More')}</span>
                              <ArrowRight className="h-3 w-3 group-hover/btn:translate-x-1 transition-transform" />
                            </Button>
                          </div>
                        </div>
                      </Link>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Industries;